import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import MessageNavbar from '@/components/webchat/teams/MessageNavbar';
import TeamMessages from '@/components/webchat/teams/TeamMessages';
import Composer from '@/components/webchat/teams/Composer';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RootState } from '@/reducers';
import type { AppDispatch } from '@/store/store';
import { getTeamConversationForCustomer } from '@/actions/teams';

// The inline panel and the fullscreen dialog can both be open, so one refresh
// per customer at a time.
const refreshingCustomers = new Set<string>();

interface CommentsProps {
	infiniteScrollHeight?: string | number;
	isCollapsed: boolean;
	onToggleCollapse: () => void;
	isActivityOverview?: boolean;
	shouldShowCloseButton?: boolean;
	showHeader?: boolean;
	showAvatar?: boolean;
	className?: string;
}

export const Comments: React.FC<CommentsProps> = ({
	infiniteScrollHeight = 'h-full',
	isCollapsed,
	onToggleCollapse,
	isActivityOverview = false,
	shouldShowCloseButton = true,
	showHeader = true,
	showAvatar = true,
	className,
}) => {
	const webchat = useSelector((state: RootState) => state.webchat);
	const _activeTeamConversation = useSelector(
		(state: RootState) => state.teams._activeTeamConversation
	);

	const _customer: string | undefined = useSelector((state: RootState) => {
		const customer = state.teams.teamConversations.find(
			(conversation: { _id: string }) =>
				conversation._id === _activeTeamConversation
		)?._customer;
		return customer?._id || customer;
	});
	const dispatch = useDispatch<AppDispatch>();

	const [isAttachmentsModalOpen, setIsAttachmentsModalOpen] = useState(false);
	const [isSidebarOpen, setIsSidebarOpen] = useState(false);

	// Only notepad members get new notes pushed live, so the notes are fetched
	// again when the panel is expanded or the tab comes back into focus.
	const wasCollapsed = useRef(isCollapsed);
	useEffect(() => {
		if (isCollapsed || !_customer) {
			wasCollapsed.current = isCollapsed;
			return undefined;
		}

		const refresh = async () => {
			if (
				document.visibilityState !== 'visible' ||
				refreshingCustomers.has(_customer)
			) {
				return;
			}

			refreshingCustomers.add(_customer);
			try {
				await dispatch(getTeamConversationForCustomer({ _customer }));
			} finally {
				refreshingCustomers.delete(_customer);
			}
		};

		// the parent has just fetched the notes when the panel first renders
		if (wasCollapsed.current) {
			refresh();
		}
		wasCollapsed.current = false;

		window.addEventListener('focus', refresh);
		document.addEventListener('visibilitychange', refresh);
		return () => {
			window.removeEventListener('focus', refresh);
			document.removeEventListener('visibilitychange', refresh);
		};
	}, [isCollapsed, _customer, dispatch]);

	const handleIsAttachmentsModalOpen = () => {
		setIsAttachmentsModalOpen(!isAttachmentsModalOpen);
	};

	// if we are rendering in the activity overview, we need to set the width to 100%
	// and we cannot render it at full height and fixed top right fixed right-0 top-0 h-screen

	let width = '';
	let position = '';
	let height = '';
	if (isActivityOverview) {
		width = 'w-full';
		height = '';
	} else {
		width = isCollapsed ? 'w-12' : 'w-[400px]';
		position = 'fixed right-0 top-0 h-screen';
	}

	return (
		<TooltipProvider>
			<div
				className={cn(
					'bg-background shadow-lg transition-all duration-300 ease-in-out z-10',
					!isActivityOverview && 'border-l',
					width,
					position,
					height,
					className
				)}
			>
				{isCollapsed ? (
					<div className="h-full flex flex-col items-center pt-24">
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									onClick={onToggleCollapse}
									variant="ghost"
									size="icon"
									className="my-2"
								>
									<ChevronLeft size={16} />
								</Button>
							</TooltipTrigger>
							<TooltipContent side="left">
								<p>Show customer notes</p>
							</TooltipContent>
						</Tooltip>
					</div>
				) : (
					<div
						className={cn(
							'flex flex-col',
							isActivityOverview
								? 'h-full min-h-0'
								: 'h-full min-h-0'
						)}
					>
						{showHeader && (
							<div className="flex-shrink-0 border-b">
								<div className="flex justify-between items-center pr-2">
									<MessageNavbar
										onSidebarToggle={() =>
											setIsSidebarOpen(!isSidebarOpen)
										}
										assignedTypeSelected={
											webchat.assignedTypeSelected
										}
										handleIsAttachmentsModalOpen={
											handleIsAttachmentsModalOpen
										}
										isAttachmentsModalOpen={
											isAttachmentsModalOpen
										}
										isCommentsCollapsed={isCollapsed}
										onCommentsCollapseChange={
											onToggleCollapse
										}
										shouldShowCloseButton={
											shouldShowCloseButton
										}
									/>
								</div>
							</div>
						)}

						<div className={cn('flex flex-1 h-0 overflow-hidden')}>
							<div
								className={cn(
									'flex flex-col w-full',
									'h-full',
									isSidebarOpen ? 'w-1/2' : 'w-full'
								)}
							>
								<div
									className={cn('flex-1 h-0 overflow-hidden')}
								>
									<TeamMessages
										key={_activeTeamConversation}
										addConversationToCustomerConversations={() => {}}
										setOpenOrClosedConversationsSelected={() => {}}
										setCustomerConversationAssignedType={() => {}}
										setShowTeams={() => {}}
										assignedTypeSelected={
											webchat.assignedTypeSelected
										}
										handleChangeDashboard={() => {}}
										infiniteScrollHeight={
											infiniteScrollHeight
										}
										showAvatar={showAvatar}
									/>
								</div>
								<div className="flex-shrink-0 border-t">
									<Composer
										key={_activeTeamConversation}
										isSidebarOpen={isSidebarOpen}
									/>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
		</TooltipProvider>
	);
};
